import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import PaperStep from '@linc-technologies/linced-common/components/paper-step';

export default class PaperStepper extends Component {
  stepComponent = PaperStep;

  // childComponents holds the registered child steps. It is reassigned (never
  // mutated in place) so the tracked getter chain recomputes. This replaces the
  // ember-composability-tools ParentMixin state, removed in ember-paper 1.0.0.
  @tracked childComponents = [];

  get linear() {
    return this.args.linear ?? true;
  }

  get vertical() {
    return this.args.vertical ?? false;
  }

  get currentStep() {
    return this.args.currentStep ?? 0;
  }

  get currentStepLabel() {
    return this.currentStep + 1;
  }

  get steps() {
    return [...this.childComponents].sort((a, b) => {
      if (a.stepNumber > b.stepNumber) {
        return 1;
      } else if (a.stepNumber < b.stepNumber) {
        return -1;
      }
      return 0;
    });
  }

  get totalSteps() {
    return this.steps.length;
  }

  get mobileHeaderLabel() {
    return `Step ${this.currentStepLabel} of ${this.totalSteps}`;
  }

  // goTo navigates by ordered index (0-based sorted position), which is what
  // `currentStep`/`isActive` compare against — not the raw `stepNumber`, which
  // exists only for sorting and may be explicit/non-zero-based.
  @action
  goTo(orderedIndex) {
    if (orderedIndex < this.totalSteps && this.args.onStepChange) {
      this.args.onStepChange(orderedIndex);
    }
  }

  @action
  nextStep() {
    if (this.currentStep < this.totalSteps) {
      let nextStep = this.currentStep + 1;
      if (this.args.onStepChange) {
        this.args.onStepChange(nextStep);
      }
      if (nextStep === this.totalSteps && this.args.onStepperCompleted) {
        this.args.onStepperCompleted();
      }
    }
  }

  @action
  previousStep() {
    if (this.currentStep > 0 && this.args.onStepChange) {
      this.args.onStepChange(this.currentStep - 1);
    }
  }

  // registerChild replaces ember-composability-tools ParentMixin#registerChild.
  registerChild(childComponent) {
    this.childComponents = [...this.childComponents, childComponent];
    // automatically set stepNumber if not manually set
    if (childComponent.stepNumber === undefined) {
      childComponent._autoStepNumber = this.childComponents.length - 1;
    }
    this.updateLabels();
  }

  // unregisterChild replaces ember-composability-tools ParentMixin#unregisterChild.
  unregisterChild(childComponent) {
    this.childComponents = this.childComponents.filter(
      (c) => c !== childComponent,
    );
    this.updateLabels();
  }

  updateLabels() {
    // update labels based on the sorted index
    this.steps.forEach((step, i) => {
      if (step.orderedIndex !== i) {
        step.orderedIndex = i;
      }
    });
  }
}
